"use client"

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../layout/ThemeProvider';

interface ClientAreaSectionProps {
  scrollY: number;
}

const ClientAreaSection = ({ scrollY }: ClientAreaSectionProps) => {
  const [searchType, setSearchType] = useState('orderNumber');
  const [orderNumber, setOrderNumber] = useState('');
  const [cpf, setCpf] = useState('');
  const { theme } = useTheme();

  return (
    <section 
      id="area-cliente" 
      className={`py-16 ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
      } relative overflow-hidden`}
      aria-label="Área do cliente"
    >
      {/* Background shapes decorativos com parallax - versão simplificada sem motion */}
      <div 
        className={`absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-[40rem] h-[40rem] rounded-full ${
          theme === 'dark' ? 'bg-gray-700/80' : 'bg-blue-50/80'
        }`}
        aria-hidden="true"
        style={{ transform: `translate(50%, -50%) translateY(${scrollY * 0.05}px)` }}
      ></div>
      <div 
        className={`absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3 w-[30rem] h-[30rem] rounded-full ${
          theme === 'dark' ? 'bg-gray-700/80' : 'bg-indigo-50/80'
        }`}
        aria-hidden="true"
        style={{ transform: `translate(-33%, 33%) translateY(${scrollY * -0.05}px)` }}
      ></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-8">
          <div className={`inline-block px-3 py-1 text-sm font-semibold ${
            theme === 'dark' 
              ? 'text-blue-300 bg-blue-900/50' 
              : 'text-blue-800 bg-blue-100'
          } rounded-full mb-2`}>
            Área do Cliente
          </div>
          <h2 className={`text-3xl md:text-4xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          } mb-3`}>
            Acompanhe Seu Serviço em Tempo Real
          </h2>
          <p className={`text-lg ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          } mb-6`}>
            Nossa plataforma exclusiva permite que você acompanhe o status do seu serviço,
            histórico de atualizações e prazos de entrega.
          </p>
          
          <div 
            className={`${
              theme === 'dark' 
                ? 'bg-gray-900 border border-gray-700' 
                : 'bg-white'
            } rounded-lg shadow-xl p-6 max-w-md mx-auto relative overflow-hidden`}
          >
            {/* Carimbo EM BREVE em diagonal */}
            <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none z-20">
              <div className={`${
                theme === 'dark' 
                  ? 'text-red-500/80 border-red-500/50' 
                  : 'text-red-600/80 border-red-600/50'
              } text-5xl font-extrabold tracking-wider transform rotate-[-30deg] border-8 border-dashed px-4 py-1 rounded-lg`}>
                EM BREVE
              </div>
            </div>
            
            <h3 className={`text-xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-4`}>Consulta de Ordem de Serviço</h3>
            
            <div className="flex space-x-1 mb-3">
              <motion.button 
                whileTap={{ scale: 0.95 }}
                className={`flex-1 py-1.5 px-3 text-sm font-medium rounded-lg transition-colors ${
                  searchType === 'orderNumber' 
                    ? 'bg-blue-600 text-white' 
                    : theme === 'dark' 
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setSearchType('orderNumber')}
                aria-pressed={searchType === 'orderNumber'}
                aria-label="Buscar por número da ordem de serviço"
              >
                Número da OS
              </motion.button>
              <motion.button 
                whileTap={{ scale: 0.95 }}
                className={`flex-1 py-1.5 px-3 text-sm font-medium rounded-lg transition-colors ${
                  searchType === 'cpf' 
                    ? 'bg-blue-600 text-white' 
                    : theme === 'dark' 
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setSearchType('cpf')}
                aria-pressed={searchType === 'cpf'}
                aria-label="Buscar por CPF"
              >
                CPF
              </motion.button>
            </div>
            
            <form>
              {searchType === 'orderNumber' ? (
                <div className="mb-4">
                  <label 
                    htmlFor="orderNumber" 
                    className={`block text-sm font-medium mb-1.5 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Número da Ordem de Serviço
                  </label>
                  <motion.input
                    whileFocus={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    type="text"
                    id="orderNumber"
                    name="orderNumber"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg ${
                      theme === 'dark' 
                        ? 'bg-gray-800 border-gray-700 text-white' 
                        : 'border-gray-300 text-gray-900'
                    } border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    aria-label="Digite o número da ordem de serviço"
                    placeholder="Digite o número da OS"
                  />
                </div>
              ) : (
                <div className="mb-4">
                  <label 
                    htmlFor="cpf" 
                    className={`block text-sm font-medium mb-1.5 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    CPF do Cliente
                  </label>
                  <motion.input
                    whileFocus={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    type="text"
                    id="cpf"
                    name="cpf"
                    value={cpf}
                    onChange={(e) => setCpf(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg ${
                      theme === 'dark' 
                        ? 'bg-gray-800 border-gray-700 text-white' 
                        : 'border-gray-300 text-gray-900'
                    } border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    aria-label="Digite o CPF do cliente"
                    placeholder="Digite o CPF (apenas números)"
                  />
                </div>
              )}
              
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="w-full py-2 px-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
                aria-label="Consultar ordem de serviço"
              >
                Consultar
              </motion.button>
            </form>
            
            <div className="mt-4 text-center">
              <p className={`text-xs ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Já possui uma conta? <a href="#" className={`${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                } font-medium hover:underline`}>Faça login</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientAreaSection;